import { useState } from 'react';
import { topCategories, type Category } from '../../../mocks/categories';
import { openWhatsApp } from '../../../utils/whatsapp';
import { trackEvent } from '../../../utils/tracking';
import InlineCitySelector from './InlineCitySelector';

interface TopCardProps {
  cat: Category;
  rank: number;
}

function TopCard({ cat, rank }: TopCardProps) {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);

  const handleClose = () => setOpen(false);

  const handleSend = (city: string) => {
    trackEvent('category_click', { category: cat.name, city, section: 'top_categories' });
    trackEvent('whatsapp_redirect', { category: cat.name, city });
    openWhatsApp(cat.name, city);
  };

  return (
    <div
      className="group relative rounded-2xl transition-all duration-300 cursor-pointer"
      style={{
        background: `linear-gradient(135deg, ${cat.gradientFrom}12, ${cat.gradientTo}08)`,
        border: `1px solid ${cat.gradientFrom}35`,
        boxShadow: `0 4px 30px ${cat.glowColor}20`,
      }}
    >
      {/* Rank badge */}
      <div
        className="absolute top-3 left-3 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold z-10"
        style={{
          background: rank === 0 ? 'linear-gradient(135deg, #f59e0b, #ef4444)' : 'rgba(255,255,255,0.1)',
          color: rank === 0 ? '#000' : '#fff',
        }}
      >
        {rank + 1}
      </div>

      {/* Clickable main area */}
      <div
        className="p-5 pt-7"
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleOpen()}
        aria-label={`${cat.name} için şehir seç`}
      >
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
          style={{ background: `${cat.gradientFrom}25`, border: `1px solid ${cat.gradientFrom}40` }}
        >
          <i className={`${cat.icon} text-xl`} style={{ color: cat.gradientFrom }} />
        </div>

        <h4
          className="text-white font-semibold text-sm mb-1"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {cat.name}
        </h4>

        <div className="h-1 rounded-full bg-white/5 mt-3 overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${Math.round(((cat.clickCount ?? 5000) / 15000) * 100)}%`,
              background: `linear-gradient(90deg, ${cat.gradientFrom}, ${cat.gradientTo})`,
            }}
          />
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-gray-600 text-xs">{(cat.clickCount ?? 0).toLocaleString('tr-TR')} seçim</span>
          <span className="text-xs flex items-center gap-1" style={{ color: cat.gradientFrom }}>
            <i className="ri-map-pin-line text-xs" /> Şehir Seç
          </span>
        </div>
      </div>

      {open && (
        <InlineCitySelector
          accentColor={cat.gradientFrom}
          targetName={cat.name}
          onClose={handleClose}
          onSend={handleSend}
        />
      )}
    </div>
  );
}

export default function TopCategoriesSection() {
  return (
    <section className="bg-[#0a0a0f] py-20 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-10 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">🔥</span>
              <span
                className="text-white font-bold text-2xl md:text-3xl"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                En Çok Seçilenler
              </span>
            </div>
            <p className="text-gray-500 text-sm">Kullanıcıların en çok tercih ettiği kategoriler</p>
          </div>
          <div
            className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#fca5a5' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            Güncel sıralama
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {topCategories.map((cat, idx) => (
            <TopCard key={cat.id} cat={cat} rank={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}
